import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import api, { API } from "@/lib/api";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import MapPreview from "@/components/MapPreview";
import { useAuth } from "@/lib/auth";
import { can, isClientRole } from "@/lib/permissions";
import { toast } from "sonner";
import {
  Upload, Send, Camera, CheckCircle2, RotateCcw, FileText, AlertTriangle,
  Image as ImageIcon, MessageSquare, ShieldCheck, ClipboardCheck, Activity, Download,
  Map, ExternalLink, MapPin, FileDown, Loader2, Package, ShieldAlert, FolderArchive,
} from "lucide-react";

const CATEGORIES = [
  "Site Photos","Excavation Photos","Bedding Photos","Pipe / Pit Installation Photos",
  "Survey Set-Out","Survey Conformance","Compaction Test Results","Concrete Docket",
  "Concrete Test Results","Material Delivery Docket","Client Approval","NCR Evidence","Other Attachments",
];

// Display labels (UI). Backend canonical is still "Complete" — we accept both directions.
const CHECKLIST_STATUSES = ["Not Started","Completed","Not Applicable","Rejected"];
// Map a UI label to the value sent to the backend. Backend now accepts both
// canonical "Complete" and the alias "Completed", so either is fine — we send
// the display string and let the server normalise.
const STATUS_TO_BACKEND = {
  "Not Started": "Not Started",
  "Completed": "Completed",        // server alias of "Complete"
  "Not Applicable": "Not Applicable",
  "Rejected": "Rejected",
  // Legacy values that may already be stored:
  "In Progress": "In Progress",
  "Complete": "Complete",
};
// Convert any stored backend value to the UI display label.
const statusToLabel = (s) => {
  if (!s) return "Not Started";
  if (s === "Complete") return "Completed";
  if (s === "In Progress" || s === "Pending") return "Not Started"; // surface legacy as Not Started in the dropdown
  return s;
};


// ---------------------------------------------------------------------------
// Phase 3 Part B — Active ITPs (V2) on this Lot
// ---------------------------------------------------------------------------
function ActiveITPsV2Card({ lot, role }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const canCreate = ["admin", "qa_manager", "site_engineer", "project_engineer"].includes(role);

  const reload = useCallback(() => {
    api.get(`/lots/${lot.id}/itp-v2`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [lot.id]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!dialogOpen) return;
    api.get(`/v2/itp-templates?status=Published`).then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, [dialogOpen]);

  const create = async () => {
    if (!selectedTemplateId) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/lots/${lot.id}/itp-v2`, { template_id: selectedTemplateId });
      toast.success(`Active ITP created (${data.template_name})`);
      setDialogOpen(false);
      setSelectedTemplateId("");
      reload();
      // Navigate the user straight into the new active ITP.
      window.location.href = `/lots/${lot.id}/itp-v2/${data.id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to create active ITP");
    } finally { setCreating(false); }
  };

  return (
    <Card className="mt-4 overflow-hidden border-teal-200" data-testid="active-itp-v2-card">
      <div className="flex items-center justify-between border-b bg-teal-50/50 px-4 py-2 text-sm font-medium text-slate-700">
        <span>Active ITPs (V2)</span>
        {canCreate && (
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="create-active-itp-v2-btn">Create ITP</Button>
        )}
      </div>
      <div className="divide-y">
        {items.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="active-itp-v2-empty">No active V2 ITPs on this lot yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`active-itp-v2-row-${a.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">{a.template_name} <span className="text-xs font-normal text-slate-500">Rev {a.template_revision}</span></div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 ${a.status === "Completed" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-teal-300 bg-teal-50 text-teal-800"}`}>{a.status}</span>
                {a.progress && (
                  <>
                    <span>· {a.progress.inspection_rows_completed || 0} / {a.progress.inspection_rows_total || 0} rows</span>
                    {(a.progress.release_points_pending || 0) > 0 && <span>· {a.progress.release_points_pending} release{a.progress.release_points_pending !== 1 ? "s" : ""} pending</span>}
                    {(a.progress.signatures_outstanding || 0) > 0 && <span>· {a.progress.signatures_outstanding} sig outstanding</span>}
                  </>
                )}
                <span>· Created {a.created_at?.slice(0, 10)} by {a.created_by_name}</span>
              </div>
            </div>
            <Link to={`/lots/${lot.id}/itp-v2/${a.id}`}>
              <Button size="sm" variant="outline" data-testid={`open-active-itp-v2-${a.id}`}>Open</Button>
            </Link>
          </div>
        ))}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="create-active-itp-v2-dialog">
          <DialogHeader><DialogTitle>Create Active ITP from Template</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500">The active ITP will be linked to this lot and the exact published template version selected. Later revisions of the template will not modify this active ITP.</div>
          <div>
            <Label>Published Template</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger data-testid="create-active-itp-v2-template-select"><SelectValue placeholder="Select a published template…" /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 && <div className="p-2 text-xs text-slate-500">No published V2 templates found.</div>}
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`create-active-itp-v2-template-option-${t.id}`}>{t.name} ({t.code || "—"})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!selectedTemplateId || creating} data-testid="create-active-itp-v2-confirm-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export default function LotDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lot, setLot] = useState(null);  const [evidence, setEvidence] = useState([]);
  const [signatureEvidence, setSignatureEvidence] = useState([]);
  const [comments, setComments] = useState([]);
  const [ncrs, setNcrs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);

  const reload = useCallback(async () => {
    const [lotR, evR, sigEvR, cmR, ncrR, auR, usR] = await Promise.all([
      api.get(`/lots/${id}`),
      api.get("/evidence", { params: { lot_id: id } }),
      api.get(`/lots/${id}/signature-evidence`).catch(() => ({ data: [] })),
      api.get("/comments", { params: { lot_id: id } }),
      api.get("/ncrs", { params: { lot_id: id } }),
      api.get("/audit-logs", { params: { lot_id: id } }),
      api.get("/users"),
    ]);
    setLot(lotR.data); setEvidence(evR.data); setSignatureEvidence(sigEvR.data || []);
    setComments(cmR.data);
    setNcrs(ncrR.data); setAudit(auR.data); setUsers(usR.data);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const [closeBlock, setCloseBlock] = useState(null); // {outstanding, surveyOutstanding}

  if (!lot) return <div className="p-8 text-slate-500">Loading…</div>;

  const totalHP = lot.hold_points.length;
  const approvedHP = lot.hold_points.filter(h => h.status === "Approved / Released").length;
  const allApproved = approvedHP === totalHP;

  const submitForReview = async () => {
    try {
      await api.post(`/lots/${id}/submit-for-review`);
      toast.success("Submitted for QA review");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const closeLot = async (overrideReason) => {
    try {
      const body = overrideReason ? { override_reason: overrideReason } : {};
      await api.post(`/lots/${id}/close`, body);
      toast.success(overrideReason ? "Lot closed with override" : "Lot closed");
      setCloseBlock(null);
      reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured Phase 4 blocker payload
      if (detail && typeof detail === "object" && (detail.error === "lot_close_blocked" || detail.error === "survey_conformance_required")) {
        setCloseBlock({
          outstanding: detail.outstanding || [],
          surveyOutstanding: detail.survey_conformance_outstanding || (detail.error === "survey_conformance_required" ? (detail.outstanding || []) : []),
        });
        return;
      }
      toast.error((typeof detail === "object" ? detail.message : detail) || "Failed");
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="lot-detail-page">
      <Link to="/lots" className="text-sm text-slate-500 hover:underline">← Lots</Link>
      <div className="mt-3 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="overline text-teal-700">{lot.lot_number}</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">{lot.activity}</h1>
          <p className="mt-1 text-sm text-slate-600">{lot.project_name} · {lot.itp_number} {lot.itp_name} · {lot.location}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={lot.status} />
          <span className="text-xs text-slate-500">{approvedHP}/{totalHP} hold points approved</span>
          {can.submitLotForReview(user?.role) && allApproved && lot.status !== "Closed" && (
            <Button onClick={submitForReview} className="bg-teal-700 hover:bg-teal-800" data-testid="submit-review-btn">
              <Send className="mr-1.5 size-4" />Submit for Review
            </Button>
          )}
          {can.closeLot(user?.role) && lot.status !== "Closed" && (
            <Button onClick={() => closeLot()} className="bg-emerald-700 hover:bg-emerald-800" data-testid="close-lot-btn">
              <CheckCircle2 className="mr-1.5 size-4" />Close Lot
            </Button>
          )}
          {isClientRole(user?.role) && (
            <span className="rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-orange-700" data-testid="client-readonly-badge">
              Client · read-only
            </span>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-6">
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-md bg-slate-100 p-1">
          {[
            ["overview","Overview"],["checklist","ITP"],["holdpoints","Hold Points / Action Points"],
            ["evidence","Evidence"],["comments","Comments"],["ncrs","NCRs"],
            ["rfis","RFIs"],["map","Map / Plan"],["signoff","Sign-Off"],["audit","Audit Trail"],
          ].map(([v,l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-white data-[state=active]:shadow">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Project", lot.project_name],["ITP", `${lot.itp_number}`],
              ["Drawing", lot.drawing_number || "—"],["Spec Ref", lot.specification_reference || "—"],
              ["Location", lot.location || "—"],["Opened", lot.date_opened?.slice(0,10)],
              ["ITP Open Date", lot.planned_inspection_date?.slice(0,10) || "—"],["Closed", lot.date_closed?.slice(0,10) || "—"],
              ["Client ITP Reference", lot.client_itp_reference_number || "—"],["Client Lot Number", lot.client_lot_number || "—"],
            ].map(([l,v]) => (
              <Card key={l} className="border-slate-200 p-3" data-testid={`lot-meta-${String(l).toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}><div className="overline">{l}</div><div className="mt-1 text-sm font-semibold text-slate-900">{v}</div></Card>
            ))}
          </div>
          <LotMetaEditCard lot={lot} role={user?.role} reload={reload} />
          <CloseOutPackageCard lot={lot} role={user?.role} />
          <ActiveITPsV2Card lot={lot} role={user?.role} />
          <CompletedITPSection lot={lot} role={user?.role} evidence={evidence} reload={reload} />
          <SurveyConformanceCard lot={lot} role={user?.role} reload={reload} />
          <LotConformanceCard lot={lot} role={user?.role} reload={reload} />
          <SupportingDocumentsCard lot={lot} role={user?.role} evidence={evidence} reload={reload} />
        </TabsContent>

        <TabsContent value="checklist" className="mt-4">
          <ChecklistTab lot={lot} reload={reload} role={user?.role} evidence={evidence} users={users} />
        </TabsContent>

        <TabsContent value="holdpoints" className="mt-4">
          <HoldPointsTab lot={lot} users={users} evidence={evidence} reload={reload} role={user?.role} />
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <EvidenceTab lot={lot} evidence={evidence} signatureEvidence={signatureEvidence} reload={reload} role={user?.role} />
        </TabsContent>

        <TabsContent value="comments" className="mt-4">
          <CommentsTab lotId={lot.id} comments={comments} reload={reload} />
        </TabsContent>

        <TabsContent value="ncrs" className="mt-4">
          <NCRTab lot={lot} ncrs={ncrs} reload={reload} role={user?.role} />
        </TabsContent>

        <TabsContent value="rfis" className="mt-4">
          <RFITab lot={lot} role={user?.role} />
        </TabsContent>

        <TabsContent value="map" className="mt-4">
          <ExternalLotMapSection lot={lot} role={user?.role} evidence={evidence} reload={reload} />
          <MapTab lot={lot} role={user?.role} />
        </TabsContent>

        <TabsContent value="signoff" className="mt-4">
          <SignOffTab lot={lot} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditTab audit={audit} lotId={lot.id} role={user?.role} />
        </TabsContent>
      </Tabs>

      {closeBlock && (
        <CloseBlockedModal
          open
          onClose={() => setCloseBlock(null)}
          outstanding={closeBlock.outstanding}
          surveyOutstanding={closeBlock.surveyOutstanding}
          canOverride={["admin", "qa_manager"].includes(user?.role)}
          onOverride={(reason) => closeLot(reason)}
        />
      )}
    </div>
  );
}
function ChecklistTab({ lot, reload, role, evidence = [], users = [] }) {
  const canEdit = can.updateChecklist(role);
  const canRequestRelease = can.requestHoldPointRelease(role);
  const [reasonDialog, setReasonDialog] = useState(null);
  const [attachItem, setAttachItem] = useState(null);
  const [signItem, setSignItem] = useState(null);     // {item, kind: "contractor"|"client"}
  const [viewSigsItem, setViewSigsItem] = useState(null);
  // Phase 7E shortcut — open existing HP / AP release request dialogs from a checklist row.
  const [releaseHP, setReleaseHP] = useState(null);   // {hp, evidenceCount}
  const [requestAp, setRequestAp] = useState(null);   // ap object
  // Phase 7D — preload signatures for all checklist items in one go.
  const [allSigs, setAllSigs] = useState({}); // {itemId: [sig...]}
  const loadAllSigs = useCallback(async () => {
    const map = {};
    await Promise.all((lot.checklist_items || []).map(async (it) => {
      try {
        const r = await api.get(`/lots/${lot.id}/checklist/${it.id}/signatures`);
        map[it.id] = r.data;
      } catch { map[it.id] = []; }
    }));
    setAllSigs(map);
  }, [lot.id, lot.checklist_items]);
  useEffect(() => { loadAllSigs(); }, [loadAllSigs]);

  const evidenceCountByItem = useMemo(() => {
    const m = {};
    for (const e of evidence) {
      if (e.checklist_item_id) m[e.checklist_item_id] = (m[e.checklist_item_id] || 0) + 1;
    }
    return m;
  }, [evidence]);

  // Resolve which HP / AP (if any) is linked to a row by hp_number, so we can
  // reuse the existing release-request dialog instead of building a new one.
  const findLinkedRelease = (it) => {
    const code = (it.linked_hold_point || "").toString().trim().toUpperCase();
    if (!code) return null;
    if (code.startsWith("AP")) {
      const ap = (lot.approval_points || []).find(a => (a.hp_number || a.ap_number || "").toString().toUpperCase() === code);
      if (!ap) return null;
      // Only show when a fresh release request is sensible.
      const canSendForApproval = ["Draft", "More Information Required", "Rejected"].includes(ap.status);
      return { kind: "ap", ap, enabled: canSendForApproval };
    }
    // Treat any other prefix as HP. Witness Points (WP) are not eligible for the existing release workflow.
    if (code.startsWith("WP")) return null;
    const hp = (lot.hold_points || []).find(h => (h.hp_number || "").toString().toUpperCase() === code);
    if (!hp) return null;
    const enabled = hp.status !== "Approved / Released" && hp.status !== "Awaiting Client Review";
    return { kind: "hp", hp, enabled };
  };

  const onRequestRelease = (it) => {
    if (!canRequestRelease) return;
    const link = findLinkedRelease(it);
    if (!link) return;
    if (link.kind === "ap") setRequestAp(link.ap);
    else {
      const hpEv = evidence.filter(e => e.hold_point_id === link.hp.id);
      setReleaseHP({ hp: link.hp, evidenceCount: hpEv.length });
    }
  };

  const sendUpdate = async (item_id, payload) => {
    try { await api.put(`/lots/${lot.id}/checklist/${item_id}`, payload); reload(); }
    catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) ? detail.message : (detail || "Failed");
      toast.error(msg);
    }
  };

  const onPickStatus = (it, label) => {
    if (!canEdit) return;
    if (label === "Not Applicable") { setReasonDialog({ kind: "na", item: it }); return; }
    if (label === "Rejected")       { setReasonDialog({ kind: "rejected", item: it }); return; }
    // Completed / Not Started → send straight through
    sendUpdate(it.id, { status: label, comments: it.comments || "" });
  };

  const sections = {};
  for (const it of lot.checklist_items) {
    sections[it.section_name] = sections[it.section_name] || [];
    sections[it.section_name].push(it);
  }
  return (
    <div className="space-y-4">
      {Object.entries(sections).map(([name, items]) => (
        <Card key={name} className="border-slate-200">
          <div className="border-b border-slate-200 px-4 py-3 font-heading text-base font-semibold">{name}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-4 py-2">Item</th>
                  <th className="px-4 py-2">HP/AP</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Evidence</th>
                  <th className="px-4 py-2">Signatures</th>
                  <th className="px-4 py-2">Comments</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <ChecklistRow
                    key={it.id} it={it} canEdit={canEdit} role={role}
                    evidenceCount={evidenceCountByItem[it.id] || 0}
                    sigs={allSigs[it.id] || []}
                    releaseInfo={findLinkedRelease(it)}
                    canRequestRelease={canRequestRelease}
                    onRequestRelease={() => onRequestRelease(it)}
                    onPickStatus={onPickStatus}
                    onAttach={() => setAttachItem(it)}
                    onSign={(kind) => setSignItem({ item: it, kind })}
                    onViewSigs={() => setViewSigsItem(it)}
                    onCommentChange={(v) => sendUpdate(it.id, { status: it.status, comments: v })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {reasonDialog && (
        <ReasonDialog
          kind={reasonDialog.kind}
          item={reasonDialog.item}
          onClose={() => setReasonDialog(null)}
          onSubmit={(reason) => {
            const payload = { status: reasonDialog.kind === "na" ? "Not Applicable" : "Rejected", comments: reasonDialog.item.comments || "" };
            if (reasonDialog.kind === "na") payload.na_reason = reason;
            else payload.rejection_reason = reason;
            sendUpdate(reasonDialog.item.id, payload).then(() => setReasonDialog(null));
          }}
        />
      )}

      {attachItem && (
        <ChecklistAttachDialog
          lot={lot}
          item={attachItem}
          onClose={() => setAttachItem(null)}
          onUploaded={() => { setAttachItem(null); reload(); }}
        />
      )}

      {signItem && (
        <ChecklistSignDialog
          lot={lot}
          item={signItem.item}
          kind={signItem.kind}
          role={role}
          onClose={() => setSignItem(null)}
          onSigned={() => { setSignItem(null); loadAllSigs(); }}
        />
      )}

      {viewSigsItem && (
        <ChecklistSignaturesViewer
          lot={lot}
          item={viewSigsItem}
          role={role}
          onClose={() => setViewSigsItem(null)}
          onSuperseded={loadAllSigs}
        />
      )}

      {releaseHP && (
        <ReleaseRequestDialog
          open={!!releaseHP}
          onClose={() => setReleaseHP(null)}
          lot={lot}
          hp={releaseHP.hp}
          evidenceCount={releaseHP.evidenceCount}
          users={users}
          reload={() => { setReleaseHP(null); reload(); }}
        />
      )}

      {requestAp && (
        <APRequestApprovalDialog
          open={!!requestAp}
          onClose={() => setRequestAp(null)}
          lot={lot}
          ap={requestAp}
          users={users}
          reload={() => { setRequestAp(null); reload(); }}
        />
      )}
    </div>
  );
}

function ChecklistRow({ it, canEdit, role, evidenceCount = 0, sigs = [], onPickStatus, onAttach, onSign, onViewSigs, onCommentChange, releaseInfo = null, canRequestRelease = false, onRequestRelease }) {
  const label = statusToLabel(it.status);
  const isNa = label === "Not Applicable";
  const isRej = label === "Rejected";
  const isComp = label === "Completed";
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`checklist-item-${it.id}`}>
      <td className="px-4 py-2">
        <div className="font-medium text-slate-900">{it.item_description}{it.mandatory && <span className="text-red-600"> *</span>}</div>
        {it.requirement && <div className="mt-0.5 text-[11px] text-slate-500">{it.requirement}</div>}
        {(isNa && it.na_reason) && (
          <div className="mt-1 rounded border-l-2 border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid={`checklist-na-reason-${it.id}`}>
            <b>N/A reason:</b> {it.na_reason}
            {it.na_by_name && <span className="text-amber-700"> — {it.na_by_name}</span>}
          </div>
        )}
        {(isRej && it.rejection_reason) && (
          <div className="mt-1 rounded border-l-2 border-rose-300 bg-rose-50 px-2 py-1 text-[11px] text-rose-900" data-testid={`checklist-rejection-reason-${it.id}`}>
            <b>Rejection reason:</b> {it.rejection_reason}
            {it.rejected_by_name && <span className="text-rose-700"> — {it.rejected_by_name}</span>}
          </div>
        )}
      </td>
      <td className="px-4 py-2 font-mono text-xs">
        {it.linked_hold_point ? (
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${String(it.linked_hold_point).toUpperCase().startsWith("AP") ? "bg-indigo-100 text-indigo-800" : "bg-teal-100 text-teal-800"}`} data-testid={`checklist-point-badge-${it.id}`}>
            {String(it.linked_hold_point).toUpperCase().startsWith("AP") ? "AP" : "HP"} · {it.linked_hold_point}
          </span>
        ) : "—"}
      </td>
      <td className="px-4 py-2">
        <Select value={label} onValueChange={(v) => onPickStatus(it, v)} disabled={!canEdit}>
          <SelectTrigger className={`h-9 w-44 ${isComp ? "border-emerald-300 bg-emerald-50" : isNa ? "border-amber-300 bg-amber-50" : isRej ? "border-rose-300 bg-rose-50" : ""}`} data-testid={`checklist-status-${it.id}`}><SelectValue/></SelectTrigger>
          <SelectContent>{CHECKLIST_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col items-start gap-1">
          {canEdit && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onAttach} data-testid={`checklist-attach-${it.id}`}>
              <Upload className="mr-1 size-3.5"/>Attach Evidence
            </Button>
          )}
          <ChecklistEvidenceCount count={evidenceCount}/>
        </div>
      </td>
      <td className="px-4 py-2">
        <ChecklistSignatureCell
          it={it} sigs={sigs} role={role}
          onSign={onSign} onViewSigs={onViewSigs}
          releaseInfo={releaseInfo}
          canRequestRelease={canRequestRelease}
          onRequestRelease={onRequestRelease}
        />
      </td>
      <td className="px-4 py-2">
        <div className="space-y-1">
          <Textarea
            defaultValue={it.comments}
            onBlur={(e) => canEdit && e.target.value !== it.comments && onCommentChange(e.target.value)}
            placeholder={canEdit ? "Inspection notes, NCR/RFI ref…" : "Read-only"}
            readOnly={!canEdit}
            rows={2}
            className="min-h-9 text-xs"
            data-testid={`checklist-comments-${it.id}`}
          />
          {it.updated_at && (
            <div className="text-[10px] text-slate-400" data-testid={`checklist-comments-meta-${it.id}`}>
              Updated{it.updated_by_name ? ` by ${it.updated_by_name}` : ""} · {new Date(it.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// Tiny stateless counter — derived from lot.evidence client-side via context.
function ChecklistEvidenceCount({ count = 0 }) {
  if (!count) return <span className="text-[10px] text-slate-400">No files</span>;
  return <span className="text-[10px] font-semibold text-teal-700">{count} file{count === 1 ? "" : "s"}</span>;
}

function ReasonDialog({ kind, item, onClose, onSubmit }) {
  const [reason, setReason] = useState(
    kind === "na" ? (item.na_reason || "") : (item.rejection_reason || "")
  );
  const isNa = kind === "na";
  const title = isNa ? "Mark as Not Applicable" : "Mark as Rejected";
  const hint = isNa
    ? "A reason is required so the audit trail and final ITP show why this item does not apply to this lot."
    : "A reason is required so the audit trail records the rejection and corrective action requirement.";
  const placeholder = isNa
    ? "e.g. Kerb interface not applicable to this lot. Lot only covers pavement reconstruction between CH 120–160."
    : "e.g. Density test report does not match the lot chainage limits. Corrected report required.";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid={`reason-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className={isNa ? "text-amber-900" : "text-rose-900"}>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 p-2 text-xs"><b>Item:</b> {item.item_description}</div>
          <p className="text-xs text-slate-600">{hint}</p>
          <Textarea
            value={reason} onChange={e => setReason(e.target.value)}
            rows={3} placeholder={placeholder}
            data-testid={`reason-textarea-${kind}`}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { if (reason.trim()) onSubmit(reason.trim()); else toast.error("Please enter a reason."); }}
            disabled={!reason.trim()}
            className={isNa ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-700 hover:bg-rose-800"}
            data-testid={`reason-submit-${kind}`}
          >
            Save {isNa ? "Not Applicable" : "Rejected"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistAttachDialog({ lot, item, onClose, onUploaded }) {
  const fileRef = useRef();
  const [comments, setComments] = useState("");
  const [uploading, setUploading] = useState(false);
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lot_id", lot.id);
      fd.append("category", "Other Attachments");
      fd.append("checklist_item_id", item.id);
      if (item.linked_hold_point) {
        const hp = (lot.hold_points || []).find(h => h.hp_number === item.linked_hold_point);
        if (hp) fd.append("hold_point_id", hp.id);
      }
      if (comments) fd.append("comments", comments);
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attached to ITP item");
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="checklist-attach-dialog">
        <DialogHeader><DialogTitle>Attach Evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 p-2 text-xs"><b>Item:</b> {item.item_description}</div>
          <div>
            <Label>File *</Label>
            <Input type="file" ref={fileRef} data-testid="checklist-attach-file"/>
            <p className="mt-1 text-[11px] text-slate-500">PDF, PNG, JPG, DOCX, XLSX supported.</p>
          </div>
          <div>
            <Label>Comments (optional)</Label>
            <Textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} placeholder="Density test against CH 120–160…" data-testid="checklist-attach-comment"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={upload} disabled={uploading} className="bg-teal-700 hover:bg-teal-800" data-testid="checklist-attach-submit">
            <Upload className="mr-1.5 size-4"/>{uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HoldPointsTab({ lot, users, evidence, reload, role }) {
  const [releaseHP, setReleaseHP] = useState(null);
  const [rfis, setRfis] = useState([]);
  const canRequest = can.requestHoldPointRelease(role);

  useEffect(() => { api.get(`/lots/${lot.id}/rfis`).then(r => setRfis(r.data)).catch(()=>{}); }, [lot.id]);
  const rfisByHp = useMemo(() => {
    const map = {};
    rfis.forEach(r => {
      const hpId = r.lot_link_hold_point_id;
      if (!hpId) return;
      map[hpId] = map[hpId] || [];
      map[hpId].push(r);
    });
    return map;
  }, [rfis]);

  return (
    <div>
      <Card className="mt-1 overflow-hidden border-slate-200">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-teal-700">
          Hold Points · {lot.hold_points.length}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">HP</th><th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Required Evidence</th>
              <th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Reviewer</th><th className="px-4 py-3">Approved</th><th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {lot.hold_points.map(hp => {
              const hpEvidence = evidence.filter(e => e.hold_point_id === hp.id);
              return (
                <tr key={hp.id} className="border-t border-slate-100" data-testid={`hp-row-${hp.hp_number}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700">{hp.hp_number}</td>
                  <td className="px-4 py-3 font-semibold">
                    {hp.title}
                    {(rfisByHp[hp.id] || []).some(r => r.status !== "Closed" && r.status !== "Superseded") && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded bg-yellow-50 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-800" data-testid={`hp-rfi-warning-${hp.hp_number}`} title="Unresolved RFI">
                        <AlertTriangle className="size-3"/>Open RFI
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={hp.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{(hp.required_evidence || []).join(", ")}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{hp.submitted_at ? new Date(hp.submitted_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{hp.reviewed_by || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{hp.reviewed_at ? new Date(hp.reviewed_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3">
                    {canRequest ? (
                      <Button
                        size="sm"
                        variant={hp.status === "Approved / Released" ? "outline" : "default"}
                        className={hp.status === "Approved / Released" ? "" : "bg-orange-600 hover:bg-orange-700"}
                        disabled={hp.status === "Approved / Released" || hp.status === "Awaiting Client Review"}
                        onClick={() => setReleaseHP({ hp, evidenceCount: hpEvidence.length })}
                        data-testid={`request-release-${hp.hp_number}`}
                      >
                        <Send className="mr-1 size-3.5" />Request Release
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400" data-testid={`request-release-disabled-${hp.hp_number}`}>
                        View only
                      </span>
                    )}
                    {hp.status === "Awaiting Client Review" && hp.review_token && (
                      <a
                        className="ml-2 text-xs font-semibold text-teal-700 underline"
                        href={`/client-review/${hp.review_token}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`view-link-${hp.hp_number}`}
                      >Open Link</a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <ApprovalPointsCard lot={lot} role={role} reload={reload} users={users} />
      {releaseHP && (
        <ReleaseRequestDialog
          open={!!releaseHP}
          onClose={() => setReleaseHP(null)}
          lot={lot}
          hp={releaseHP.hp}
          evidenceCount={releaseHP.evidenceCount}
          users={users}
          reload={() => { setReleaseHP(null); reload(); }}
        />
      )}
    </div>
  );
}

const AP_STATUSES = ["Draft", "Submitted", "Awaiting Approval", "Approved", "Rejected", "More Information Required", "Closed"];
const AP_EDITOR_ROLES = new Set(["admin", "qa_manager", "site_engineer"]);
const AP_REQUEST_ROLES = new Set(["admin", "qa_manager", "site_engineer", "project_engineer"]);

function ApprovalPointsCard({ lot, role, reload, users = [] }) {
  const aps = lot.approval_points || [];
  const canEdit = AP_EDITOR_ROLES.has(role);
  const canRequest = AP_REQUEST_ROLES.has(role);
  const [requestAp, setRequestAp] = useState(null);

  const update = async (ap, newStatus, comments) => {
    try {
      await api.put(`/lots/${lot.id}/approval-points/${ap.id}`, {
        status: newStatus,
        comments: comments ?? ap.comments ?? "",
      });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Card className="mt-4 overflow-hidden border-indigo-200" data-testid="approval-points-card">
      <div className="border-b border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-indigo-700">
        Action Points · {aps.length}
      </div>
      {aps.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-slate-500" data-testid="approval-points-empty">
          No approval points on this lot.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">AP</th><th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Required Evidence</th>
              <th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Reviewer</th>
              <th className="px-4 py-3">Decided</th><th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {aps.map(ap => {
              const canSendForApproval = canRequest && (ap.status === "Draft" || ap.status === "More Information Required" || ap.status === "Rejected");
              return (
                <tr key={ap.id} className="border-t border-slate-100" data-testid={`ap-row-${ap.hp_number}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono font-semibold text-indigo-700">{ap.hp_number}</div>
                    <span className="mt-1 inline-block rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-800">AP</span>
                  </td>
                  <td className="px-4 py-3 font-semibold">{ap.title}</td>
                  <td className="px-4 py-3"><StatusBadge status={ap.status}/></td>
                  <td className="px-4 py-3 text-slate-600">{(ap.required_evidence || []).join(", ")}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{ap.submitted_at ? new Date(ap.submitted_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{ap.reviewed_by || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{ap.reviewed_at ? new Date(ap.reviewed_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {canSendForApproval && (
                        <Button
                          size="sm"
                          className="h-9 bg-indigo-700 hover:bg-indigo-800"
                          onClick={() => setRequestAp(ap)}
                          data-testid={`request-ap-approval-${ap.hp_number}`}
                        >
                          <Send className="mr-1 size-3.5"/>Submit Action Point
                        </Button>
                      )}
                      {ap.review_token && ap.status !== "Approved" && (
                        <a
                          href={`/approval-review/${ap.review_token}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center rounded-md border border-indigo-300 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                          data-testid={`view-ap-approval-link-${ap.hp_number}`}
                        >
                          <ExternalLink className="mr-1 size-3.5"/>View Action Point Link
                        </a>
                      )}
                      {canEdit ? (
                        <Select value={ap.status} onValueChange={(v) => update(ap, v)}>
                          <SelectTrigger className="h-9 w-44" data-testid={`ap-status-${ap.hp_number}`}><SelectValue/></SelectTrigger>
                          <SelectContent>{AP_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-slate-400" data-testid={`ap-status-readonly-${ap.hp_number}`}>View only</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {requestAp && (
        <APRequestApprovalDialog
          open={!!requestAp}
          onClose={() => setRequestAp(null)}
          lot={lot}
          ap={requestAp}
          users={users}
          reload={() => { setRequestAp(null); reload(); }}
        />
      )}
    </Card>
  );
}

function APRequestApprovalDialog({ open, onClose, lot, ap, users, reload }) {
  const [pool, setPool] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !lot?.project_id) return;
    setSelectedIds([]);
    api.get(`/projects/${lot.project_id}/client-reviewers`).then(r => setPool(r.data || []))
      .catch(() => setPool((users || []).filter(u => u.role === "client" && u.is_active !== false)));
  }, [open, lot?.project_id]);

  const toggle = (id) => setSelectedIds((cur) => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  const selectAll = () => setSelectedIds(pool.filter(u => u.is_active !== false).map(u => u.id));

  const submit = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one reviewer"); return; }
    setSubmitting(true);
    try {
      await api.post(`/lots/${lot.id}/approval-points/${ap.id}/request-approval`, {
        reviewer_ids: selectedIds,
        engineer_comments: comments,
      });
      toast.success(`Approval request sent to ${selectedIds.length} reviewer(s) for ${ap.hp_number}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ap-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="size-5 text-indigo-700"/>Submit Action Point — {ap.hp_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="border-indigo-200 bg-indigo-50/40 p-3">
            <div className="text-xs text-slate-600">Action Point</div>
            <div className="font-semibold">{ap.hp_number} — {ap.title}</div>
            {ap.description && <div className="mt-1 text-xs text-slate-600">{ap.description}</div>}
          </Card>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Client Reviewer(s) — pick one, multiple, or all *</Label>
              <button type="button" onClick={selectAll} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="ap-select-all-reviewers">Select all active</button>
            </div>
            <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="ap-reviewer-pool">
              {pool.length === 0 && (
                <p className="px-2 py-3 text-xs text-slate-500">
                  No Client Reviewers in this project&apos;s pool yet. Add reviewers from the Project page first.
                </p>
              )}
              {pool.map(u => (
                <label key={u.id} className="flex items-start gap-2 rounded px-1 py-1 hover:bg-slate-50 text-sm">
                  <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`ap-reviewer-checkbox-${u.id}`} className="mt-0.5"/>
                  <span>
                    <span className="font-semibold text-slate-900">{u.name}</span>
                    <span className="ml-1 text-xs text-slate-500">{u.email}</span>
                    <span className="block text-xs text-slate-500">{u.company || ""}{u.role_title ? ` · ${u.role_title}` : ""}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-500">Each selected reviewer receives an individual secure link. Whoever signs first records the decision.</p>
          </div>
          <div>
            <Label className="text-xs">Engineer Comments</Label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder="Summary of what is being submitted for approval and any notes for the reviewer."
              data-testid="ap-engineer-comments"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || selectedIds.length === 0} className="bg-indigo-700 hover:bg-indigo-800" data-testid="ap-request-submit">
            {submitting ? "Sending…" : <><Send className="mr-1.5 size-4"/>{`Submit to ${selectedIds.length || ""} Reviewer${selectedIds.length === 1 ? "" : "s"}`}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ReleaseRequestDialog({ open, onClose, lot, hp, evidenceCount, users, reload }) {
  const [pool, setPool] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !lot?.project_id) return;
    setSelectedIds([]);
    api.get(`/projects/${lot.project_id}/client-reviewers`).then(r => setPool(r.data || []))
      .catch(() => {
        // Fallback: project pool endpoint may be 403/404 for current user — fall back to global client users.
        setPool((users || []).filter(u => u.role === "client" && u.is_active !== false));
      });
  }, [open, lot?.project_id]);

  const toggle = (id) => setSelectedIds((cur) => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  const selectAll = () => setSelectedIds(pool.filter(u => u.is_active !== false).map(u => u.id));

  const submit = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one reviewer"); return; }
    setSubmitting(true);
    try {
      await api.post(`/lots/${lot.id}/hold-points/${hp.id}/request-release`, {
        reviewer_ids: selectedIds,
        engineer_comments: comments,
      });
      toast.success(`Release request sent to ${selectedIds.length} reviewer(s). See Email Outbox.`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="release-request-dialog">
        <DialogHeader><DialogTitle>Confirm Hold Point Release Request</DialogTitle></DialogHeader>
        <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
          <Row k="Project" v={lot.project_name}/>
          <Row k="Lot Number" v={lot.lot_number}/>
          <Row k="ITP Reference" v={`${lot.itp_number} — ${lot.itp_name}`}/>
          <Row k="Hold Point" v={`${hp.hp_number} — ${hp.title}`}/>
          <Row k="Location / Chainage" v={lot.location || "—"}/>
          <Row k="Evidence Attached" v={`${evidenceCount} file(s)`}/>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Client Reviewer(s) — pick one, multiple, or all *</Label>
            <button type="button" onClick={selectAll} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="hp-select-all-reviewers">Select all active</button>
          </div>
          <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="hp-reviewer-pool">
            {pool.length === 0 && (
              <p className="px-2 py-3 text-xs text-slate-500">
                No Client Reviewers in this project&apos;s pool yet. Add reviewers from the Project page first.
              </p>
            )}
            {pool.map(u => (
              <label key={u.id} className="flex items-start gap-2 rounded px-1 py-1 hover:bg-slate-50 text-sm">
                <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`hp-reviewer-checkbox-${u.id}`} className="mt-0.5"/>
                <span>
                  <span className="font-semibold text-slate-900">{u.name}</span>
                  <span className="ml-1 text-xs text-slate-500">{u.email}</span>
                  <span className="block text-xs text-slate-500">{u.company || ""}{u.role_title ? ` · ${u.role_title}` : ""}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">Each selected reviewer receives an individual secure link. Whoever signs first releases the hold point.</p>
        </div>
        <div>
          <Label>Engineer Comments</Label>
          <Textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Add context for the reviewer…" data-testid="engineer-comments"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || selectedIds.length === 0} className="bg-orange-600 hover:bg-orange-700" data-testid="send-release-btn">
            <Send className="mr-1.5 size-4" />{submitting ? "Sending…" : `Send to ${selectedIds.length || ""} Reviewer${selectedIds.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) { return <div className="flex gap-2"><span className="w-44 shrink-0 text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span></div>; }

function EvidenceTab({ lot, evidence, signatureEvidence = [], reload, role }) {
  const fileRef = useRef();
  const [category, setCategory] = useState("Site Photos");
  const [hpId, setHpId] = useState("none");
  const [checklistItemId, setChecklistItemId] = useState("none");
  const [rfiId, setRfiId] = useState("none");
  const [comments, setComments] = useState("");
  const [gps, setGps] = useState(null); // {lat, lng, accuracy} | null
  const [gpsError, setGpsError] = useState("");
  const [gpsCapturing, setGpsCapturing] = useState(false);
  const [rfis, setRfis] = useState([]);
  const [uploading, setUploading] = useState(false);
  const canUpload = can.uploadEvidence(role);

  useEffect(() => {
    if (!canUpload) return;
    api.get(`/lots/${lot.id}/rfis`).then(r => setRfis(r.data)).catch(() => setRfis([]));
  }, [lot.id, canUpload]);

  const captureGps = () => {
    setGpsError("");
    if (!navigator.geolocation) {
      setGpsError("GPS location could not be captured. You can still upload evidence without location.");
      return;
    }
    setGpsCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsCapturing(false);
        toast.success("GPS location captured.");
      },
      () => {
        setGpsCapturing(false);
        setGpsError("GPS location could not be captured. You can still upload evidence without location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const resetForm = () => {
    setComments("");
    setGps(null);
    setGpsError("");
    setChecklistItemId("none");
    setRfiId("none");
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lot_id", lot.id);
      fd.append("category", category);
      if (hpId && hpId !== "none") fd.append("hold_point_id", hpId);
      if (checklistItemId && checklistItemId !== "none") fd.append("checklist_item_id", checklistItemId);
      if (rfiId && rfiId !== "none") fd.append("rfi_id", rfiId);
      if (comments) fd.append("comments", comments);
      if (gps) {
        fd.append("gps_lat", String(gps.lat));
        fd.append("gps_lng", String(gps.lng));
      }
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      resetForm();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {canUpload ? (
        <Card className="border-slate-200 p-4" data-testid="evidence-upload-form">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="evidence-category"><SelectValue/></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked Hold Point</Label>
              <Select value={hpId} onValueChange={setHpId}>
                <SelectTrigger data-testid="evidence-hp"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {lot.hold_points.map(hp => <SelectItem key={hp.id} value={hp.id}>{hp.hp_number} — {hp.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked ITP Item <span className="text-slate-400">(optional)</span></Label>
              <Select value={checklistItemId} onValueChange={setChecklistItemId}>
                <SelectTrigger data-testid="evidence-checklist-item"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {lot.checklist_items.map(ci => (
                    <SelectItem key={ci.id} value={ci.id}>
                      {ci.section_name} — {ci.item_description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked RFI <span className="text-slate-400">(optional)</span></Label>
              {rfis.length === 0 ? (
                <div className="mt-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="evidence-rfi-empty">
                  No RFIs linked to this lot.
                </div>
              ) : (
                <Select value={rfiId} onValueChange={setRfiId}>
                  <SelectTrigger data-testid="evidence-rfi"><SelectValue placeholder="None"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {rfis.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.rfi_number} — {r.title} — {r.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="md:col-span-2">
              <Label>Comments</Label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add evidence comments, inspection notes, or location details."
                rows={2}
                data-testid="evidence-comments"
              />
            </div>
            <div className="md:col-span-2">
              <Label>GPS / Location <span className="text-slate-400">(optional)</span></Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" onClick={captureGps} disabled={gpsCapturing} data-testid="capture-gps-btn" className="h-11">
                  <MapPin className="mr-1.5 size-4"/>{gpsCapturing ? "Capturing…" : "Capture GPS Location"}
                </Button>
                {gps && (
                  <div className="rounded-md bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900" data-testid="evidence-gps-result">
                    <b>GPS location captured.</b> {gps.lat.toFixed(6)}, {gps.lng.toFixed(6)}
                    {gps.accuracy != null && <> · accuracy ±{Math.round(gps.accuracy)} m</>}
                  </div>
                )}
                {!gps && gpsError && <div className="text-xs text-red-700" data-testid="evidence-gps-error">{gpsError}</div>}
              </div>
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <input ref={fileRef} type="file" className="hidden" data-testid="evidence-file-input" onChange={(e) => upload(e.target.files?.[0])} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"/>
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-12 bg-teal-700 hover:bg-teal-800" data-testid="upload-file-btn">
                <Upload className="mr-2 size-4" />{uploading ? "Uploading…" : "Upload File"}
              </Button>
              <input id="cam" type="file" capture="environment" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="evidence-camera-input"/>
              <label htmlFor="cam" className="inline-flex h-12 cursor-pointer items-center rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                <Camera className="mr-2 size-4" />Photo (Mobile)
              </label>
            </div>
          </div>
        </Card>
      ) : (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="evidence-readonly-note">
          You have read-only access to evidence on this lot.
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
        {evidence.map(e => <EvidenceCard key={e.id} item={e} />)}
        {evidence.length === 0 && <div className="col-span-full text-sm text-slate-500">No evidence uploaded yet.</div>}
      </div>

      {signatureEvidence.length > 0 && (
        <div className="mt-6" data-testid="signature-evidence-section">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-slate-900">Signature Evidence
              <span className="ml-2 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">Derived · read-only</span>
            </h3>
            <span className="text-[11px] text-slate-500">Automatically surfaced from per-checklist-item signatures.</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
            {signatureEvidence.map(s => <SignatureEvidenceCard key={s.id} item={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function SignatureEvidenceCard({ item }) {
  const ts = (item.uploaded_at || "").slice(0, 19).replace("T", " ");
  return (
    <Card className="overflow-hidden border-teal-200" data-testid={`signature-evidence-card-${item.id}`}>
      <div className="aspect-square bg-white">
        {item.signature_image_b64 ? (
          <img src={item.signature_image_b64} alt="signature" className="size-full object-contain p-2" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-slate-500">
            <ShieldCheck className="size-10" />
            <span className="mt-1 text-[10px] uppercase">Typed</span>
          </div>
        )}
      </div>
      <div className="space-y-1 p-2">
        <div className="overline text-teal-700">{item.category}</div>
        <div className="truncate text-xs font-semibold text-slate-900" title={item.file_name}>{item.file_name}</div>
        <div className="text-[10px] text-slate-600">
          <b>{item.signer_name || "—"}</b>{item.signer_company ? ` · ${item.signer_company}` : ""}
        </div>
        <div className="flex flex-wrap gap-1">
          <span className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">SIG</span>
          {item.decision && item.decision !== "signed" && (
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${item.decision === "rejected" ? "bg-rose-50 text-rose-700" : item.decision === "correction_required" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
              {item.decision.replace("_", " ")}
            </span>
          )}
        </div>
        {item.comments && <div className="line-clamp-2 text-[10px] text-slate-600" title={item.comments}>{item.comments}</div>}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">{ts}</span>
          <span className="text-[10px] font-semibold text-slate-500">{item.source || "—"}</span>
        </div>
      </div>
    </Card>
  );
}

function EvidenceCard({ item }) {
  const isImage = item.file_type?.startsWith("image/");
  const open = () => window.open(`${api.defaults.baseURL}/evidence/${item.id}/download`, "_blank");
  return (
    <Card className="overflow-hidden border-slate-200" data-testid={`evidence-card-${item.id}`}>
      <div className="aspect-square bg-slate-100">
        {isImage ? (
          <AuthImage fileId={item.id} alt={item.file_name} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-slate-500">
            <FileText className="size-10" />
            <span className="mt-1 text-[10px] uppercase">{item.file_type?.split("/").pop()}</span>
          </div>
        )}
      </div>
      <div className="space-y-1 p-2">
        <div className="overline">{item.category}</div>
        <div className="truncate text-xs font-semibold text-slate-900" title={item.file_name}>{item.file_name}</div>
        <div className="flex flex-wrap gap-1">
          {item.hold_point_id && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700" title="Linked hold point">HP</span>}
          {item.checklist_item_id && <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700" title="Linked checklist item">CL</span>}
          {item.rfi_id && <span className="rounded bg-yellow-50 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-800" title="Linked RFI">RFI</span>}
          {(item.gps_lat != null && item.gps_lng != null) && (
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800" title={`${item.gps_lat.toFixed(5)}, ${item.gps_lng.toFixed(5)}`}>GPS</span>
          )}
        </div>
        {item.comments && <div className="line-clamp-2 text-[10px] text-slate-600" title={item.comments}>{item.comments}</div>}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">{new Date(item.uploaded_at).toLocaleDateString()}</span>
          <button onClick={open} className="text-[10px] font-semibold text-teal-700">Open</button>
        </div>
      </div>
    </Card>
  );
}

function AuthImage({ fileId, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/evidence/${fileId}/download`, { responseType: "blob" })
      .then(r => { if (!cancel) setUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { cancel = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [fileId]);
  if (!url) return <div className="grid h-full place-items-center"><ImageIcon className="size-10 text-slate-400" /></div>;
  return <img src={url} alt={alt} className="size-full object-cover" />;
}

function CommentsTab({ lotId, comments, reload }) {
  const [body, setBody] = useState("");
  const post = async () => {
    if (!body.trim()) return;
    try { await api.post("/comments", { lot_id: lotId, body }); setBody(""); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <Card className="border-slate-200 p-4">
        <Textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="Add a comment…" data-testid="comment-input"/>
        <div className="mt-2 flex justify-end">
          <Button onClick={post} className="bg-teal-700 hover:bg-teal-800" data-testid="comment-submit"><MessageSquare className="mr-1.5 size-4"/>Post</Button>
        </div>
      </Card>
      <div className="mt-3 space-y-2">
        {comments.map(c => (
          <Card key={c.id} className="border-slate-200 p-3" data-testid={`comment-${c.id}`}>
            <div className="flex justify-between text-xs text-slate-500">
              <span><b className="text-slate-900">{c.user_name}</b> · {c.role.replace("_"," ")}</span>
              <span>{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <div className="mt-1 text-sm">{c.body}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function NCRTab({ lot, ncrs, reload, role }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ issue: "", specification_requirement: "", actual_result: "", root_cause: "", corrective_action: "", preventive_action: "" });
  const canRaise = can.raiseNCR(role);

  const create = async () => {
    if (!form.issue) { toast.error("Issue required"); return; }
    try { await api.post("/ncrs", { ...form, lot_id: lot.id }); setOpen(false); reload(); toast.success("NCR raised"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      {canRaise && (
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setOpen(true)} variant="outline" data-testid="raise-ncr-btn"><AlertTriangle className="mr-1.5 size-4 text-red-600"/>Raise NCR</Button>
        </div>
      )}
      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-2">NCR</th><th className="px-4 py-2">Issue</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Due</th></tr>
          </thead>
          <tbody>
            {ncrs.map(n => (
              <tr key={n.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono font-semibold">{n.ncr_number}</td>
                <td className="px-4 py-2">{n.issue}</td>
                <td className="px-4 py-2"><StatusBadge status={n.status}/></td>
                <td className="px-4 py-2">{n.due_date || "—"}</td>
              </tr>
            ))}
            {ncrs.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No NCRs</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Raise NCR</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {[["issue","Issue *"],["specification_requirement","Spec Requirement"],["actual_result","Actual Result"],["root_cause","Root Cause"],["corrective_action","Corrective Action"],["preventive_action","Preventive Action"]].map(([k,l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Textarea value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} rows={2} data-testid={`ncr-field-${k}`}/>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-red-600 hover:bg-red-700" data-testid="ncr-save">Raise NCR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SignOffTab({ lot }) {
  const released = lot.hold_points.filter(h => h.status === "Approved / Released");
  return (
    <div className="space-y-3">
      {released.length === 0 && <div className="text-sm text-slate-500">No client sign-offs recorded yet.</div>}
      {released.map(hp => {
        const r = hp.approval_record;
        if (!r) return null;
        return (
          <Card key={hp.id} className="border-slate-200 p-4" data-testid={`signoff-${hp.hp_number}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-emerald-700">Released</div>
                <div className="font-heading text-lg font-bold">{hp.hp_number} — {hp.title}</div>
              </div>
              <StatusBadge status={hp.status}/>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
              <div><div className="overline">Reviewer</div><div className="font-semibold">{r.reviewer_name}</div></div>
              <div><div className="overline">Company</div><div className="font-semibold">{r.company || "—"}</div></div>
              <div><div className="overline">Role</div><div className="font-semibold">{r.role || "—"}</div></div>
              <div><div className="overline">Timestamp</div><div className="font-semibold">{new Date(r.timestamp).toLocaleString()}</div></div>
              <div className="md:col-span-2"><div className="overline">Comments</div><div>{r.comments || "—"}</div></div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function AuditTab({ audit, lotId, role }) {
  const canDownload = ["admin", "qa_manager", "site_engineer", "project_engineer", "platform_admin"].includes(role);
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/lots/${lotId}/audit-trail.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const disp = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(disp);
      const fname = m ? m[1] : `Audit_Trail_${lotId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Audit trail PDF downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    }
  };
  return (
    <div className="space-y-2">
      {canDownload && (
        <div className="flex justify-end">
          <Button data-testid="download-audit-pdf-btn" onClick={downloadPdf} variant="outline" className="border-slate-200">
            <FileDown className="mr-2 size-4" /> Download Audit Trail PDF
          </Button>
        </div>
      )}
      {audit.map(a => (
        <Card key={a.id} className="border-slate-200 p-3" data-testid={`audit-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Activity className="size-3.5 text-slate-400"/>
              <span className="font-mono text-xs text-slate-500">{new Date(a.timestamp).toLocaleString()}</span>
              <span className="font-semibold text-slate-900">{a.user_name}</span>
              <span className="overline">{a.role}</span>
            </div>
            <span className="font-mono text-xs font-bold text-teal-700">{a.action}</span>
          </div>
          {(a.previous_status || a.new_status) && (
            <div className="mt-1 text-xs text-slate-600">
              {a.previous_status && <>From <b>{a.previous_status}</b> →</>} <b>{a.new_status}</b>
            </div>
          )}
          {a.comment && <div className="mt-1 text-xs text-slate-700">{a.comment}</div>}
        </Card>
      ))}
      {audit.length === 0 && <div className="text-sm text-slate-500">No audit entries yet.</div>}
    </div>
  );
}

function SurveyConformanceCard({ lot, role, reload }) {
  const canRequest = ["admin", "qa_manager", "site_engineer", "project_engineer"].includes(role);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    surveyor_name: "", surveyor_email: "", surveyor_company: "",
    document_type: "Lot Conformance Report", request_message: "",
    due_date: "", drawing_number: lot?.drawing_number || "",
    required_before_close_out: true,
  });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/lots/${lot.id}/survey-requests`);
      setRequests(r.data || []);
    } catch (e) {
      setRequests([]);
    } finally { setLoading(false); }
  }, [lot.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.surveyor_name || !form.surveyor_email) { toast.error("Surveyor name and email are required"); return; }
    setSubmitting(true);
    try {
      await api.post(`/lots/${lot.id}/survey-requests`, {
        ...form,
        due_date: form.due_date || null,
      });
      toast.success("Survey conformance request sent.");
      setOpen(false);
      setForm({ surveyor_name: "", surveyor_email: "", surveyor_company: "", document_type: "Lot Conformance Report", request_message: "", due_date: "", drawing_number: lot?.drawing_number || "" });
      await load();
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send request");
    } finally { setSubmitting(false); }
  };

  const resend = async (req) => {
    try {
      await api.post(`/lots/${lot.id}/survey-requests/${req.id}/resend`);
      toast.success("Reminder sent.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  const statusStyle = (s) => ({
    Requested: "bg-amber-50 text-amber-700 border-amber-200",
    Received: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  }[s] || "bg-slate-50 text-slate-700 border-slate-200");

  const overdue = (r) => r.status === "Requested" && r.due_date && new Date(r.due_date) < new Date();

  return (
    <Card className="mt-4 border-slate-200 p-5" data-testid="survey-conformance-card">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="overline text-teal-700">Survey / Lot Conformance</div>
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Conformance reports</h2>
        </div>
        {canRequest && (
          <Button data-testid="request-survey-btn" onClick={() => setOpen(true)} className="bg-teal-700 hover:bg-teal-800">
            <Send className="mr-2 size-4" /> Request Lot Conformance Report
          </Button>
        )}
      </div>

      {loading && <div className="mt-3 text-sm text-slate-500">Loading…</div>}
      {!loading && requests.length === 0 && (
        <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="survey-empty">
          No conformance report requested yet.
        </div>
      )}
      {!loading && requests.length > 0 && (
        <div className="mt-3 space-y-2">
          {requests.map((r) => (
            <div key={r.id} data-testid={`survey-row-${r.id}`} className="rounded-md border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">{r.document_type} · {r.surveyor_name}{r.surveyor_company ? ` · ${r.surveyor_company}` : ""}</div>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusStyle(r.status)}`}>
                  {overdue(r) ? "Overdue" : r.status}
                </span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-1 text-xs text-slate-600 sm:grid-cols-4">
                <div><span className="overline text-slate-400">Requested</span><div>{r.requested_at?.slice(0,10)} · {r.requested_by_name}</div></div>
                <div><span className="overline text-slate-400">Due</span><div>{r.due_date || "—"}</div></div>
                <div><span className="overline text-slate-400">Uploaded</span><div>{r.received_at?.slice(0,10) || "—"}</div></div>
                <div><span className="overline text-slate-400">File</span><div className="truncate">{r.uploaded_filename || "—"}</div></div>
              </div>
              {r.uploaded_comments && <div className="mt-1 text-xs text-slate-700">Surveyor comment: {r.uploaded_comments}</div>}
              {r.request_message && r.status !== "Received" && <div className="mt-1 text-xs text-slate-500">Request message: {r.request_message}</div>}
              {canRequest && r.status === "Requested" && (
                <div className="mt-2 flex gap-2">
                  <Button data-testid={`survey-resend-${r.id}`} variant="outline" size="sm" onClick={() => resend(r)}><RotateCcw className="mr-1 size-3"/>Resend reminder</Button>
                  {r.upload_link && (
                    <a className="text-xs text-teal-700 underline" href={r.upload_link} target="_blank" rel="noreferrer">Copy upload link</a>
                  )}
                </div>
              )}
              {r.evidence_file_id && (
                <div className="mt-2 text-xs">
                  <a href={`${API}/evidence/${r.evidence_file_id}/download`} target="_blank" rel="noreferrer" className="font-semibold text-teal-700 underline">
                    Download uploaded report
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Request Lot Conformance Report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Surveyor name *</Label><Input data-testid="req-surveyor-name" value={form.surveyor_name} onChange={(e) => setForm({ ...form, surveyor_name: e.target.value })} /></div>
              <div><Label>Surveyor email *</Label><Input data-testid="req-surveyor-email" type="email" value={form.surveyor_email} onChange={(e) => setForm({ ...form, surveyor_email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Surveyor company</Label><Input data-testid="req-surveyor-company" value={form.surveyor_company} onChange={(e) => setForm({ ...form, surveyor_company: e.target.value })} /></div>
              <div><Label>Due date</Label><Input data-testid="req-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Document type</Label>
                <select data-testid="req-doc-type" value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option>Lot Conformance Report</option>
                  <option>Survey Conformance Report</option>
                  <option>As-built Survey</option>
                  <option>Set-out Verification</option>
                  <option>Other</option>
                </select>
              </div>
              <div><Label>Drawing reference</Label><Input data-testid="req-drawing" value={form.drawing_number} onChange={(e) => setForm({ ...form, drawing_number: e.target.value })} /></div>
            </div>
            <div>
              <Label>Request message / instructions</Label>
              <Textarea data-testid="req-message" rows={3} value={form.request_message} onChange={(e) => setForm({ ...form, request_message: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="req-required-closeout" type="checkbox" checked={form.required_before_close_out} onChange={(e) => setForm({ ...form, required_before_close_out: e.target.checked })} />
              <span>Required before lot close-out</span>
              <span className="text-xs text-slate-500">— blocks final close until received</span>
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="req-submit-btn" onClick={submit} disabled={submitting || !form.surveyor_name || !form.surveyor_email} className="bg-teal-700 hover:bg-teal-800">
              {submitting ? "Sending…" : <><Send className="mr-2 size-4" /> Send request</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ChecklistSignatureCell({ it, sigs = [], role, onSign, onViewSigs, releaseInfo = null, canRequestRelease = false, onRequestRelease }) {
  const active = sigs.filter(s => !s.is_superseded);
  const contractor = active.find(s => s.signature_type === "contractor_signature");
  const client = active.find(s => s.signature_type === "client_signature" || s.signature_type === "client_release_signature");
  const reqC = !!it.requires_contractor_signature;
  const reqCL = !!(it.requires_client_signature || it.requires_client_release);
  const canSignContractor = ["admin", "qa_manager", "site_engineer", "subcontractor", "project_engineer"].includes(role);
  const canSignClient = ["admin", "qa_manager"].includes(role);
  // Release shortcut visibility: only when row is linked to an HP/AP that's in
  // a state where a fresh release request is sensible, and the user can request.
  const showRelease = canRequestRelease && releaseInfo && releaseInfo.enabled;
  const releaseLabel = releaseInfo?.kind === "ap" ? "Request AP Approval" : "Request Client Release";
  const releaseTestId = releaseInfo?.kind === "ap"
    ? `row-request-ap-approval-${it.id}`
    : `row-request-release-${it.id}`;

  const label = (s, req) => {
    if (s) {
      if (s.decision === "rejected") return ["Rejected", "bg-rose-100 text-rose-800 border-rose-200"];
      if (s.decision === "correction_required") return ["Correction", "bg-amber-100 text-amber-800 border-amber-200"];
      if (s.decision === "released" || s.signature_type === "client_release_signature") return ["Released", "bg-emerald-100 text-emerald-800 border-emerald-200"];
      return ["Signed", "bg-emerald-100 text-emerald-800 border-emerald-200"];
    }
    if (req) return ["Pending", "bg-slate-100 text-slate-700 border-slate-200"];
    return ["Not Required", "bg-slate-50 text-slate-500 border-slate-100"];
  };
  const [cLab, cCls] = label(contractor, reqC);
  const [clLab, clCls] = label(client, reqCL);
  return (
    <div className="space-y-1" data-testid={`sig-cell-${it.id}`}>
      <div className="flex items-center gap-1">
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${cCls}`}>Contractor: {cLab}</span>
        {canSignContractor && (
          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => onSign("contractor")} data-testid={`sign-contractor-${it.id}`}>Sign</Button>
        )}
      </div>
      <div className="flex items-center gap-1">
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${clCls}`}>Client: {clLab}</span>
        {canSignClient && (
          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => onSign("client")} data-testid={`sign-client-${it.id}`}>Sign</Button>
        )}
      </div>
      <button onClick={onViewSigs} className="text-[10px] font-semibold text-teal-700 hover:underline" data-testid={`view-sigs-${it.id}`}>
        View ({active.length})
      </button>
      {showRelease && (
        <Button
          size="sm"
          variant="outline"
          className="mt-1 h-7 px-2 text-[10px] border-orange-300 text-orange-700 hover:bg-orange-50"
          onClick={onRequestRelease}
          data-testid={releaseTestId}
          title={`Send the existing ${releaseInfo.kind === "ap" ? "Action Point approval" : "Hold Point release"} request for this row`}
        >
          <Send className="mr-1 size-3" />{releaseLabel}
        </Button>
      )}
    </div>
  );
}

function ChecklistSignDialog({ lot, item, kind, role, onClose, onSigned }) {
  // kind: "contractor" | "client"
  const isClient = kind === "client";
  const isHPorAP = ["hold_point", "action_point"].includes(_detectFrontendItemType(item));
  const [signerName, setSignerName] = useState("");
  const [company, setCompany] = useState("");
  const [signerRole, setSignerRole] = useState("");
  const [decision, setDecision] = useState(isClient ? "released" : "signed");
  const [comment, setComment] = useState("");
  const [method, setMethod] = useState("drawn");
  const [imgData, setImgData] = useState("");
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  // Canvas drawing
  const draw = (e, isStart) => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX) || 0) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY) || 0) - rect.top;
    if (isStart) { ctx.beginPath(); ctx.moveTo(x, y); drawingRef.current = true; return; }
    if (!drawingRef.current) return;
    ctx.lineTo(x, y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.stroke();
  };
  const clearPad = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setImgData("");
  };
  const capture = () => {
    const c = canvasRef.current;
    setImgData(c ? c.toDataURL("image/png") : "");
  };

  const isRejectish = isClient && (decision === "rejected" || decision === "correction_required");
  const submit = async () => {
    if (!signerName.trim()) { toast.error("Signer name required"); return; }
    if (method === "drawn" && !imgData && canvasRef.current) capture();
    if (isRejectish && !comment.trim()) { toast.error("Reason/comment required for rejection."); return; }
    setSaving(true);
    try {
      const payload = {
        signer_name: signerName.trim(),
        signer_company: company.trim() || null,
        signer_role: signerRole.trim() || null,
        signature_type: isClient ? (decision === "released" && isHPorAP ? "client_release_signature" : "client_signature") : "contractor_signature",
        signature_image_b64: method === "drawn" ? imgData : null,
        signature_method: method,
        decision: isClient ? decision : "signed",
        comment: comment.trim() || null,
      };
      await api.post(`/lots/${lot.id}/checklist/${item.id}/signature`, payload);
      toast.success("Signature saved");
      onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid={`sign-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{isClient ? "Client Signature" : "Contractor Signature"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded bg-slate-50 p-2 text-xs"><b>Item:</b> {item.item_description}</div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Signer name *</Label><Input value={signerName} onChange={e=>setSignerName(e.target.value)} data-testid="signer-name"/></div>
            <div><Label className="text-xs">Company</Label><Input value={company} onChange={e=>setCompany(e.target.value)} data-testid="signer-company"/></div>
            <div className="col-span-2"><Label className="text-xs">Role / Title</Label><Input value={signerRole} onChange={e=>setSignerRole(e.target.value)} data-testid="signer-role"/></div>
            {isClient && (
              <div className="col-span-2">
                <Label className="text-xs">Decision</Label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger data-testid="signer-decision"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="released">Released / Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="correction_required">Correction Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="signer-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="drawn">Drawn (finger / stylus / mouse)</SelectItem>
                  <SelectItem value="typed">Typed (no image)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {method === "drawn" && (
            <div>
              <div className="overflow-hidden rounded border border-slate-300 bg-white">
                <canvas
                  ref={canvasRef} width={480} height={140}
                  className="block w-full touch-none"
                  onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); draw(e, true); }}
                  onPointerMove={(e) => draw(e, false)}
                  onPointerUp={() => { drawingRef.current = false; capture(); }}
                  data-testid="signature-pad"
                />
              </div>
              <div className="mt-1 flex justify-between text-xs">
                <button type="button" onClick={clearPad} className="font-semibold text-slate-700 hover:underline" data-testid="signature-clear">Clear Signature</button>
                <span className="text-[11px] text-slate-500">Sign with finger, stylus, or mouse.</span>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Comment {isRejectish && <span className="text-rose-700">(required)</span>}</Label>
            <Textarea value={comment} onChange={e=>setComment(e.target.value)} rows={2} placeholder={isRejectish ? "Reason for rejection / correction required…" : "Optional"} data-testid="signer-comment"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="sign-submit">{saving ? "Saving…" : "Submit Signature"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistSignaturesViewer({ lot, item, role, onClose, onSuperseded }) {
  const [sigs, setSigs] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const canSupersede = ["admin", "qa_manager"].includes(role);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/lots/${lot.id}/checklist/${item.id}/signatures`, { params: showHistory ? { include_superseded: true } : {} });
      setSigs(r.data);
    } catch { setSigs([]); }
  }, [lot.id, item.id, showHistory]);
  useEffect(() => { load(); }, [load]);

  const supersede = async (sigId) => {
    if (!window.confirm("Supersede this signature? It will remain in audit history but no longer count as active.")) return;
    try {
      await api.delete(`/lots/${lot.id}/checklist/${item.id}/signature/${sigId}`);
      toast.success("Signature superseded");
      onSuperseded(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="signatures-viewer">
        <DialogHeader><DialogTitle>Signatures · {item.item_description}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={showHistory} onChange={e => setShowHistory(e.target.checked)} data-testid="show-superseded"/>
            Show superseded history
          </label>
          {sigs.length === 0 && <div className="text-sm text-slate-500">No signatures yet.</div>}
          {sigs.map(s => (
            <div key={s.id} className={`rounded border border-slate-200 p-3 text-xs ${s.is_superseded ? "opacity-60" : ""}`} data-testid={`sig-row-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900">
                    {s.signature_type} · {s.decision || "signed"} {s.is_superseded && <span className="text-rose-700">(superseded)</span>}
                  </div>
                  <div className="text-slate-600">{s.signer_name}{s.signer_company ? ` · ${s.signer_company}` : ""}{s.signer_role ? ` · ${s.signer_role}` : ""}</div>
                  <div className="text-[10px] text-slate-500">{(s.signed_at || "").slice(0, 19).replace("T", " ")}{s.source ? ` · ${s.source}` : ""}</div>
                  {s.comment && <div className="mt-1 text-slate-700"><b>Comment:</b> {s.comment}</div>}
                </div>
                {s.signature_image_b64 && (
                  <img src={s.signature_image_b64} alt="signature" className="h-12 rounded border border-slate-200 bg-white" />
                )}
              </div>
              {canSupersede && !s.is_superseded && (
                <button onClick={() => supersede(s.id)} className="mt-2 text-[11px] font-semibold text-rose-700 hover:underline" data-testid={`supersede-${s.id}`}>
                  Supersede Signature
                </button>
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function _detectFrontendItemType(item) {
  const hp = (item.linked_hold_point || "").toString().toUpperCase();
  if (hp.startsWith("AP")) return "action_point";
  if (hp.startsWith("WP")) return "witness_point";
  if (hp) return "hold_point";
  return "checkpoint";
}

function ExternalLotMapSection({ lot, role, evidence, reload }) {
  const canUpload = ["admin", "site_engineer", "qa_manager"].includes(role);
  const [showUpload, setShowUpload] = useState(false);
  // External lot maps = evidence rows where category="Lot Map" OR source="external_upload".
  const lotMaps = useMemo(
    () => (evidence || []).filter(e => (e.category || "").toLowerCase() === "lot map" || e.source === "external_upload"),
    [evidence]
  );
  return (
    <Card className="mb-4 border-slate-200" data-testid="external-lot-map-section">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div>
          <div className="overline text-teal-700">Per-lot external upload</div>
          <h3 className="font-heading text-base font-bold tracking-tight text-slate-900">Lot Map / External Markup</h3>
          <p className="text-xs text-slate-500">Upload an external drawing markup (Bluebeam, survey sketch, client-issued plan, etc.) and link it to this lot.</p>
        </div>
        {canUpload && (
          <Button onClick={() => setShowUpload(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="upload-external-lot-map">
            <Upload className="mr-1.5 size-4"/>Upload External Lot Map
          </Button>
        )}
      </div>
      {lotMaps.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-slate-500" data-testid="external-lot-map-empty">
          No external lot maps uploaded yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-4 py-2">File</th>
                <th className="px-4 py-2">Drawing No.</th>
                <th className="px-4 py-2">Rev</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {lotMaps.map(e => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`external-lot-map-row-${e.id}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{e.file_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.drawing_number || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.revision || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{e.description || "—"}</td>
                  <td className="px-4 py-2 text-[11px]">
                    <span className="rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800">External Upload</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{e.uploaded_by_name} · {(e.uploaded_at || "").slice(0,10)}</td>
                  <td className="px-4 py-2 text-right">
                    <a
                      className="text-xs font-semibold text-teal-700 hover:underline"
                      href={`${API}/evidence/${e.id}/download`}
                      target="_blank" rel="noreferrer"
                      data-testid={`external-lot-map-download-${e.id}`}
                    >Download</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showUpload && (
        <ExternalLotMapUploadDialog
          lot={lot}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
        />
      )}
    </Card>
  );
}

function ExternalLotMapUploadDialog({ lot, onClose, onUploaded }) {
  const fileRef = useRef();
  const [drawingNumber, setDrawingNumber] = useState("");
  const [revision, setRevision] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lot_id", lot.id);
      fd.append("category", "Lot Map");
      fd.append("source", "external_upload");
      if (drawingNumber) fd.append("drawing_number", drawingNumber);
      if (revision) fd.append("revision", revision);
      if (description) fd.append("description", description);
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("External lot map uploaded");
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="external-lot-map-upload-dialog">
        <DialogHeader><DialogTitle>Upload External Lot Map</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-600">Use this for Bluebeam markups, marked-up PDFs, survey sketches, or any drawing not created in FlowQA.</p>
          <div>
            <Label>File *</Label>
            <Input type="file" ref={fileRef} accept=".pdf,.png,.jpg,.jpeg,.docx" data-testid="external-lot-map-file"/>
            <p className="mt-1 text-[11px] text-slate-500">PDF, PNG, JPG, DOCX supported.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Drawing Number (optional)</Label>
              <Input value={drawingNumber} onChange={e => setDrawingNumber(e.target.value)} placeholder="e.g. DR-035" data-testid="external-lot-map-drawing-number"/>
            </div>
            <div>
              <Label>Revision (optional)</Label>
              <Input value={revision} onChange={e => setRevision(e.target.value)} placeholder="e.g. C" data-testid="external-lot-map-revision"/>
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              placeholder="e.g. Bluebeam markup of pavement extents between CH 120–160" data-testid="external-lot-map-description"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={uploading} className="bg-teal-700 hover:bg-teal-800" data-testid="external-lot-map-submit">
            <Upload className="mr-1.5 size-4"/>{uploading ? "Uploading…" : "Upload Lot Map"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseBlockedModal({ open, onClose, outstanding = [], surveyOutstanding = [], canOverride, onOverride }) {
  const [overrideReason, setOverrideReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="close-blocked-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="size-5"/>ITP cannot be closed
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-600">The following items are outstanding:</p>
          <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-2 text-xs">
            {surveyOutstanding.length > 0 && (
              <div>
                <div className="font-semibold text-rose-800">Survey Conformance</div>
                {surveyOutstanding.map((s, i) => (
                  <div key={i} className="mt-1 rounded bg-rose-50 px-2 py-1 text-rose-900" data-testid={`block-survey-${i}`}>
                    {s.document_type} — {s.status} ({s.surveyor_email})
                  </div>
                ))}
              </div>
            )}
            {outstanding.map((o, i) => (
              <div key={i} className="rounded bg-rose-50 px-2 py-1 text-rose-900" data-testid={`block-row-${i}`}>
                <div className="font-semibold">
                  {{
                    checklist_item: "ITP Item",
                    hold_point: "Hold Point",
                    action_point: "Action Point",
                  }[o.type] || o.type}
                  {o.code ? ` — ${o.code}` : ""}{o.title ? ` ${o.title ? "·" : ""} ${o.title}` : ""}
                </div>
                <div className="text-rose-700">Issue: {o.issue}</div>
              </div>
            ))}
          </div>
          {canOverride && (
            <div>
              <Label className="text-xs">Override reason (Admin / QA Manager)</Label>
              <Textarea
                value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                rows={2} placeholder="Reason for closing despite the unresolved items above…"
                data-testid="close-override-reason"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Override is recorded in the audit trail with all outstanding items at the time of close.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Back to lot</Button>
          {canOverride && (
            <Button
              onClick={async () => {
                if (!overrideReason.trim()) { toast.error("Override reason required."); return; }
                setSubmitting(true);
                try {
                  await onOverride(overrideReason.trim());
                } finally { setSubmitting(false); }
              }}
              disabled={!overrideReason.trim() || submitting}
              className="bg-rose-700 hover:bg-rose-800"
              data-testid="close-override-submit"
            >
              {submitting ? "Closing…" : "Override & Close Lot"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MapTab({ lot, role }) {
  const canEditMarkup = can.editMarkup(role);
  const [data, setData] = useState([]);
  const reload = () => api.get(`/lots/${lot.id}/markups`).then(r => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [lot.id]);

  if (data.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="map-empty">
        <Map className="mx-auto size-10 text-slate-400" />
        <div className="mt-2 font-heading text-lg font-bold">No lot map linked yet.</div>
        <p className="mt-1 text-sm text-slate-500">Upload a plan and mark up this lot area to see it here.</p>
        <Link to="/plans"><Button className="mt-4 bg-teal-700 hover:bg-teal-800" data-testid="link-plan-btn">Link Plan / Add Markup</Button></Link>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.map(({ markup, plan }) => (
        <Card key={markup.id} className="border-slate-200 p-4" data-testid={`map-card-${markup.id}`}>
          <div className="flex flex-col items-start justify-between gap-3 md:flex-row">
            <div className="min-w-0 flex-1">
              <div className="overline text-teal-700">{plan?.drawing_number} · Rev {plan?.revision}</div>
              <div className="font-heading text-lg font-bold tracking-tight text-slate-900">{plan?.title}</div>
              <div className="text-xs text-slate-600">{plan?.discipline}</div>
              <div className="mt-2 text-sm">
                <b>Markup:</b> {markup.label || `(${markup.markup_type})`} · {markup.markup_type} · {markup.coordinates?.length} pts
              </div>
              {markup.description && <p className="mt-1 text-xs text-slate-600">{markup.description}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to={`/plans/${plan?.id}`}>
                  <Button size="sm" className="bg-teal-700 hover:bg-teal-800" data-testid={`open-plan-${markup.id}`}>
                    <ExternalLink className="mr-1.5 size-4" />Open Plan
                  </Button>
                </Link>
                {canEditMarkup && (
                  <>
                    <Link to={`/plans/${plan?.id}`}>
                      <Button size="sm" variant="outline" data-testid={`edit-markup-${markup.id}`}>Edit Markup</Button>
                    </Link>
                    <Link to="/plans">
                      <Button size="sm" variant="outline">Link Another Plan</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="w-full max-w-md shrink-0">
              <MapPreview planId={plan?.id} markups={[markup]} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

const RFI_STATUSES = ["Draft", "Submitted", "Under Review", "Response Received", "Closed", "Superseded"];

function RFITab({ lot, role }) {
  const [rfis, setRfis] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", status: "Submitted", response_required_by: "",
    hold_point_id: "none",
  });
  const canCreate = can.raiseNCR(role);

  const reload = () => api.get(`/lots/${lot.id}/rfis`).then(r => setRfis(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [lot.id]);

  const create = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      await api.post("/rfis", {
        project_id: lot.project_id,
        title: form.title,
        description: form.description,
        status: form.status,
        response_required_by: form.response_required_by || null,
        lot_id: lot.id,
        itp_id: lot.itp_template_id,
        hold_point_id: form.hold_point_id !== "none" ? form.hold_point_id : null,
      });
      toast.success("RFI created");
      setOpen(false);
      setForm({ title: "", description: "", status: "Submitted", response_required_by: "", hold_point_id: "none" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      {canCreate && (
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setOpen(true)} variant="outline" data-testid="raise-rfi-btn">
            <FileText className="mr-1.5 size-4 text-teal-600"/>Raise RFI
          </Button>
        </div>
      )}
      <Card className="overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-2">RFI</th><th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Status</th><th className="px-4 py-2">Raised By</th>
              <th className="px-4 py-2">Date Raised</th><th className="px-4 py-2">Required By</th>
              <th className="px-4 py-2">Hold Point</th>
            </tr>
          </thead>
          <tbody>
            {rfis.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No RFIs linked to this lot.</td></tr>}
            {rfis.map(r => {
              const hp = (lot.hold_points || []).find(h => h.id === r.lot_link_hold_point_id);
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rfi-row-${r.rfi_number}`}>
                  <td className="px-4 py-2 font-mono font-semibold">{r.rfi_number}</td>
                  <td className="px-4 py-2">{r.title}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status}/></td>
                  <td className="px-4 py-2 text-slate-600">{r.raised_by_name}</td>
                  <td className="px-4 py-2 text-slate-500">{r.date_raised && new Date(r.date_raised).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-slate-500">{r.response_required_by ? new Date(r.response_required_by).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-teal-700">{hp ? hp.hp_number : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="raise-rfi-dialog">
          <DialogHeader><DialogTitle>Raise RFI</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="e.g. Pit connection detail clarification" data-testid="rfi-title"/></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})} rows={4} data-testid="rfi-description"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                  <SelectTrigger data-testid="rfi-status"><SelectValue/></SelectTrigger>
                  <SelectContent>{RFI_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Response Required By</Label><Input type="date" value={form.response_required_by} onChange={e=>setForm({...form, response_required_by:e.target.value})} data-testid="rfi-required-by"/></div>
            </div>
            <div>
              <Label>Link to Hold Point (optional)</Label>
              <Select value={form.hold_point_id} onValueChange={v=>setForm({...form, hold_point_id:v})}>
                <SelectTrigger data-testid="rfi-hp"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {lot.hold_points.map(hp => <SelectItem key={hp.id} value={hp.id}>{hp.hp_number} — {hp.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-teal-700 hover:bg-teal-800" data-testid="rfi-save">Raise RFI</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const GENERATE_ROLES = new Set(["admin", "qa_manager", "site_engineer"]);

function CompletedITPSection({ lot, role, evidence = [], reload: parentReload }) {
  const canGenerate = GENERATE_ROLES.has(role);
  const [outputs, setOutputs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const missingRequired = (lot.supporting_documents || []).filter(s => s.required && !s.attached);

  const reload = useCallback(() => {
    api.get(`/lots/${lot.id}/generated-itps`).then(r => setOutputs(r.data)).catch(() => setOutputs([]));
  }, [lot.id]);

  useEffect(() => { reload(); }, [reload]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/lots/${lot.id}/generate-completed-itp`);
      toast.success(`Generated ${data.filename}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const download = async (out) => {
    try {
      const r = await api.get(`/generated-itps/${out.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      let fname = (out.filename || "completed_itp.docx").replace(/[^\w. -]+/g, "_");
      if (!/\.docx$/i.test(fname)) fname += ".docx";
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  return (
    <Card className="mt-4 border-slate-200 p-4" data-testid="completed-itp-section">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <div className="overline text-teal-700">Document Output</div>
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Completed ITP</div>
          <p className="text-xs text-slate-600">
            Generate a Word (.docx) export of this lot stamped with ITP items, hold-point sign-offs and evidence references.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canGenerate && (
            <Button
              onClick={generate}
              disabled={generating}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="generate-completed-itp-btn"
            >
              {generating ? (
                <><Loader2 className="mr-2 size-4 animate-spin"/>Generating…</>
              ) : (
                <><FileDown className="mr-2 size-4"/>Generate Completed ITP</>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const tok = localStorage.getItem("token");
                const r = await fetch(`${API}/lots/${lot.id}/completed-itp.pdf`, { headers: { Authorization: `Bearer ${tok}` } });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = new Blob([await r.blob()], { type: "application/pdf" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                const closed = lot.status === "Closed";
                const safeLot = String(lot.lot_number || "Lot").replace(/[^\w.-]+/g, "_");
                a.download = `Completed_ITP_${safeLot}_${closed ? "Completed_Closed" : "Draft_NotClosed"}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 1500);
                toast.success("PDF downloaded");
              } catch (e) {
                toast.error("PDF export failed");
              }
            }}
            data-testid="export-completed-itp-pdf-btn"
          >
            <FileText className="mr-2 size-4"/>Export Completed ITP (PDF)
          </Button>
        </div>
      </div>

      {!canGenerate && outputs.length === 0 && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="completed-itp-readonly-empty">
          No completed ITP has been generated for this lot yet.
        </div>
      )}

      {canGenerate && missingRequired.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="missing-supporting-docs-warning">
          <b>{missingRequired.length} required supporting document{missingRequired.length !== 1 ? "s" : ""} not attached:</b>{" "}
          {missingRequired.map(d => d.name).join(", ")}. You can still generate, but it will be flagged in the export.
        </div>
      )}

      {outputs.length > 0 && (
        <div className="mt-4">
          <div className="overline mb-2">Generated outputs ({outputs.length})</div>
          <div className="space-y-2">
            {outputs.map((o, idx) => (
              <div
                key={o.id}
                className={`flex flex-col items-start justify-between gap-2 rounded-md border p-3 md:flex-row md:items-center ${idx === 0 ? "border-teal-200 bg-teal-50/40" : "border-slate-200 bg-white"}`}
                data-testid={`completed-itp-row-${o.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900" title={o.filename}>{o.filename}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    Generated by <b className="text-slate-700">{o.generated_by_name}</b> · {new Date(o.generated_at).toLocaleString()}
                    {o.size != null && <> · {Math.max(1, Math.round(o.size / 1024))} KB</>}
                    {idx === 0 && <span className="ml-2 rounded bg-teal-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Latest</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => download(o)}
                  className="border-teal-700 text-teal-700 hover:bg-teal-50"
                  data-testid={`download-completed-itp-${o.id}`}
                >
                  <Download className="mr-1.5 size-4"/> Download .docx
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}


const LOT_META_EDITOR_ROLES = new Set(["admin", "qa_manager", "site_engineer", "project_engineer"]);

function LotConformanceCard({ lot, role, reload }) {
  const canEdit = LOT_META_EDITOR_ROLES.has(role);
  const initial = lot.lot_conformance || {};
  const [form, setForm] = useState({
    lot_creator_name: initial.lot_creator_name || "",
    lot_creator_role: initial.lot_creator_role || "",
    parent_lot: initial.parent_lot || "",
    date_works_completed: (initial.date_works_completed || "").slice(0, 10),
    lot_fully_conforms: initial.lot_fully_conforms || "Pending",
    ncr_reference: initial.ncr_reference || "",
    date_lot_closed: (initial.date_lot_closed || "").slice(0, 10),
    verifier_name: initial.verifier_name || "",
    verifier_role: initial.verifier_role || "",
    verifier_date: (initial.verifier_date || "").slice(0, 10),
    comments: initial.comments || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.lot_fully_conforms === "Subject to NCR" && !form.ncr_reference) {
      toast.error("NCR reference is required when 'Subject to NCR' is selected.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/lots/${lot.id}/conformance`, form);
      toast.success("Lot conformance saved.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-4 border-slate-200 p-4" data-testid="lot-conformance-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-teal-700">Lot Conformance</div>
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Conformance Sign-Off</div>
        </div>
        <StatusBadge status={form.lot_fully_conforms === "Subject to NCR" ? "Subject to NCR" : (form.lot_fully_conforms === "Yes" ? "Approved" : "Pending")}/>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs">Lot creator — name</Label>
          <Input value={form.lot_creator_name} onChange={(e) => setForm({ ...form, lot_creator_name: e.target.value })} disabled={!canEdit} data-testid="conf-creator-name"/>
        </div>
        <div>
          <Label className="text-xs">Lot creator — title/role</Label>
          <Input value={form.lot_creator_role} onChange={(e) => setForm({ ...form, lot_creator_role: e.target.value })} disabled={!canEdit} data-testid="conf-creator-role"/>
        </div>
        <div>
          <Label className="text-xs">Parent / underlying lot</Label>
          <Input value={form.parent_lot} onChange={(e) => setForm({ ...form, parent_lot: e.target.value })} disabled={!canEdit} data-testid="conf-parent-lot"/>
        </div>
        <div>
          <Label className="text-xs">Date works completed</Label>
          <Input type="date" value={form.date_works_completed} onChange={(e) => setForm({ ...form, date_works_completed: e.target.value })} disabled={!canEdit} data-testid="conf-works-completed"/>
        </div>
        <div>
          <Label className="text-xs">Lot fully conforms</Label>
          <Select value={form.lot_fully_conforms} onValueChange={(v) => setForm({ ...form, lot_fully_conforms: v })} disabled={!canEdit}>
            <SelectTrigger data-testid="conf-conforms"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Yes">Yes — fully conforms</SelectItem>
              <SelectItem value="Subject to NCR">Subject to NCR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.lot_fully_conforms === "Subject to NCR" && (
          <div>
            <Label className="text-xs text-red-700">NCR reference *</Label>
            <Input value={form.ncr_reference} onChange={(e) => setForm({ ...form, ncr_reference: e.target.value })} placeholder="e.g. NCR-2026-014" disabled={!canEdit} data-testid="conf-ncr-ref"/>
          </div>
        )}
        <div>
          <Label className="text-xs">Date lot closed</Label>
          <Input type="date" value={form.date_lot_closed} onChange={(e) => setForm({ ...form, date_lot_closed: e.target.value })} disabled={!canEdit} data-testid="conf-date-closed"/>
        </div>
        <div>
          <Label className="text-xs">Verifier / signatory name</Label>
          <Input value={form.verifier_name} onChange={(e) => setForm({ ...form, verifier_name: e.target.value })} disabled={!canEdit} data-testid="conf-verifier-name"/>
        </div>
        <div>
          <Label className="text-xs">Verifier / signatory role</Label>
          <Input value={form.verifier_role} onChange={(e) => setForm({ ...form, verifier_role: e.target.value })} disabled={!canEdit} data-testid="conf-verifier-role"/>
        </div>
        <div>
          <Label className="text-xs">Verifier date</Label>
          <Input type="date" value={form.verifier_date} onChange={(e) => setForm({ ...form, verifier_date: e.target.value })} disabled={!canEdit} data-testid="conf-verifier-date"/>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Comments</Label>
          <Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} disabled={!canEdit} data-testid="conf-comments"/>
        </div>
      </div>
      {canEdit && (
        <div className="mt-3 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-conformance">
            <CheckCircle2 className="mr-2 size-4"/>{saving ? "Saving…" : "Save Conformance"}
          </Button>
        </div>
      )}
      {initial.updated_at && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="conf-meta">
          Last updated by {initial.updated_by_name || "—"} · {new Date(initial.updated_at).toLocaleString()}
        </div>
      )}
    </Card>
  );
}

function SupportingDocumentsCard({ lot, role, evidence = [], reload }) {
  const canEdit = LOT_META_EDITOR_ROLES.has(role);
  const rows = lot.supporting_documents || [];
  const requiredCount = rows.filter(r => r.required).length;
  const attachedCount = rows.filter(r => r.required && r.attached).length;
  const [busyRow, setBusyRow] = useState(null);

  const update = async (row, patch) => {
    setBusyRow(row.id);
    try {
      await api.put(`/lots/${lot.id}/supporting-documents/${row.id}`, patch);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyRow(null);
    }
  };

  const autoDetect = async () => {
    setBusyRow("__auto__");
    try {
      const { data } = await api.post(`/lots/${lot.id}/supporting-documents/auto-detect`);
      toast.success(`Auto-detected ${data.updated} row(s)`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <Card className="mt-4 border-slate-200 p-4" data-testid="supporting-docs-card">
      <div className="flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
        <div>
          <div className="overline text-teal-700">Supporting Documents</div>
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Required Evidence Pack</div>
          <p className="text-xs text-slate-600" data-testid="supporting-docs-progress">
            Required documents attached: <b>{attachedCount} / {requiredCount}</b>
          </p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={autoDetect} disabled={busyRow === "__auto__"} data-testid="auto-detect-supporting-btn">
            <ShieldCheck className="mr-1 size-4"/>{busyRow === "__auto__" ? "Detecting…" : "Auto-detect Attached"}
          </Button>
        )}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead className="bg-slate-50 text-left uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2">Supporting Document</th>
              <th className="px-3 py-2 w-24">Required</th>
              <th className="px-3 py-2 w-24">Attached</th>
              <th className="px-3 py-2">Linked Evidence</th>
              <th className="px-3 py-2">Details / Comments</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const missing = r.required && !r.attached;
              return (
                <tr key={r.id} className={`border-t border-slate-100 ${missing ? "bg-amber-50/50" : ""}`} data-testid={`supporting-row-${r.id}`}>
                  <td className="px-3 py-2 font-semibold">
                    {r.name}
                    {missing && (
                      <div className="mt-0.5 text-[10px] font-semibold text-amber-700" data-testid={`supporting-missing-${r.id}`}>
                        Required supporting document missing
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Checkbox checked={!!r.required} disabled={!canEdit} onCheckedChange={(v) => update(r, { required: !!v })} data-testid={`supporting-required-${r.id}`}/>
                  </td>
                  <td className="px-3 py-2">
                    <Checkbox checked={!!r.attached} disabled={!canEdit} onCheckedChange={(v) => update(r, { attached: !!v })} data-testid={`supporting-attached-${r.id}`}/>
                  </td>
                  <td className="px-3 py-2">
                    <EvidenceLinker row={r} evidence={evidence} canEdit={canEdit} onSave={(ids) => update(r, { linked_evidence_ids: ids })}/>
                  </td>
                  <td className="px-3 py-2">
                    <Textarea
                      rows={2}
                      defaultValue={r.details}
                      onBlur={(e) => canEdit && e.target.value !== r.details && update(r, { details: e.target.value })}
                      disabled={!canEdit}
                      placeholder={canEdit ? "Reference, test report, exceptions…" : ""}
                      className="text-xs"
                      data-testid={`supporting-details-${r.id}`}
                    />
                    {r.updated_at && (
                      <div className="mt-0.5 text-[10px] text-slate-400">
                        Updated{r.updated_by_name ? ` by ${r.updated_by_name}` : ""} · {new Date(r.updated_at).toLocaleString()}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No supporting documents configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EvidenceLinker({ row, evidence, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(row.linked_evidence_ids || []);
  const linked = evidence.filter(e => (row.linked_evidence_ids || []).includes(e.id));
  const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  return (
    <div className="space-y-1">
      <div className="text-[11px] text-slate-600">
        {linked.length} linked
        {linked.length > 0 && (
          <span className="ml-1 text-slate-400">· {linked.slice(0, 2).map(e => e.file_name).join(", ")}{linked.length > 2 ? "…" : ""}</span>
        )}
      </div>
      {canEdit && (
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setSel(row.linked_evidence_ids || []); setOpen(true); }} data-testid={`supporting-link-evidence-btn-${row.id}`}>
          Link Evidence
        </Button>
      )}
      {open && (
        <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-lg" data-testid={`evidence-linker-dialog-${row.id}`}>
            <DialogHeader>
              <DialogTitle>Link evidence — {row.name}</DialogTitle>
            </DialogHeader>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {evidence.length === 0 && <div className="text-xs text-slate-500">No evidence uploaded to this lot yet.</div>}
              {evidence.map(e => (
                <label key={e.id} className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-2 hover:bg-slate-50">
                  <Checkbox checked={sel.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`evidence-link-toggle-${row.id}-${e.id}`}/>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold">{e.file_name}</div>
                    <div className="text-[10px] text-slate-500">{e.category} · {(e.uploaded_at || "").slice(0, 10)}</div>
                  </div>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => { onSave(sel); setOpen(false); }} className="bg-teal-700 hover:bg-teal-800" data-testid={`supporting-link-save-${row.id}`}>Save Links</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}



// --------------------------------------------------------------------------- //
// Lot Meta Edit — Client ITP Reference Number + Client Lot Number quick edit
// --------------------------------------------------------------------------- //
const LOT_META_EDIT_ROLES = new Set(["admin", "qa_manager", "site_engineer", "project_engineer"]);

function LotMetaEditCard({ lot, role, reload }) {
  const canEdit = LOT_META_EDIT_ROLES.has(role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    client_itp_reference_number: lot.client_itp_reference_number || "",
    client_lot_number: lot.client_lot_number || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/lots/${lot.id}/meta`, form);
      toast.success("Lot reference updated");
      setOpen(false);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  if (!canEdit) return null;

  return (
    <div className="mt-3 flex justify-end" data-testid="lot-meta-edit-row">
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="edit-client-references-btn">
        <FileText className="mr-1.5 size-3.5" /> Edit Client References
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="edit-client-references-dialog">
          <DialogHeader><DialogTitle>Edit Client References</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client ITP Reference Number</Label>
              <Input
                data-testid="client-itp-ref-input"
                value={form.client_itp_reference_number}
                onChange={(e) => setForm({ ...form, client_itp_reference_number: e.target.value })}
                placeholder="e.g. 1841-T100-WGI-QAC-ITP-1024"
              />
              <p className="mt-1 text-xs text-slate-500">Used in Close-Out Package filename, root folder, and Completed ITP DOCX name.</p>
            </div>
            <div>
              <Label>Client Lot Number</Label>
              <Input
                data-testid="client-lot-number-input"
                value={form.client_lot_number}
                onChange={(e) => setForm({ ...form, client_lot_number: e.target.value })}
                placeholder="e.g. CL-001"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-client-references-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Close-Out Package — full ZIP download with blocker modal
// --------------------------------------------------------------------------- //
const CLOSEOUT_VIEW_ROLES = new Set(["admin", "qa_manager", "site_engineer", "project_engineer", "platform_admin"]);

function CloseOutPackageCard({ lot, role }) {
  const canView = CLOSEOUT_VIEW_ROLES.has(role);
  const [preflight, setPreflight] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [blocker, setBlocker] = useState(null); // { missing_items, can_force, message }

  const refresh = useCallback(async () => {
    if (!canView) return;
    try {
      const r = await api.get(`/lots/${lot.id}/closeout-package/preflight`);
      setPreflight(r.data);
    } catch {
      setPreflight(null);
    }
  }, [canView, lot.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const doDownload = async (force = false) => {
    setDownloading(true);
    try {
      let r;
      try {
        r = await api.get(
          `/lots/${lot.id}/closeout-package.zip${force ? "?force=true" : ""}`,
          { responseType: "blob", validateStatus: () => true }
        );
      } catch (err) {
        toast.error(err?.message || "Download failed");
        return;
      }
      if (r.status === 409) {
        // Parse blob to JSON for the blocker modal
        let data = {};
        try {
          const text = await r.data.text();
          data = JSON.parse(text) || {};
        } catch { data = {}; }
        setBlocker({
          missing_items: data.detail?.missing_items || [],
          can_force: !!data.detail?.can_force,
          message: data.detail?.message || "Outstanding close-out items",
        });
        return;
      }
      if (r.status === 403) {
        let msg = "Permission denied";
        try {
          const text = await r.data.text();
          const data = JSON.parse(text);
          msg = data.detail || msg;
        } catch { /* keep default */ }
        toast.error(msg);
        return;
      }
      if (r.status !== 200) {
        toast.error(`Download failed (HTTP ${r.status})`);
        return;
      }
      // Extract filename from Content-Disposition if present
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      let fname = (m ? m[1] : `${lot.lot_number || "Lot"}_CloseOut_Package.zip`).replace(/[^\w. -]+/g, "_");
      if (!/\.zip$/i.test(fname)) fname += ".zip";
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (force) {
        toast.success("Forced download recorded in audit trail.");
      } else {
        toast.success("Close-Out package downloaded.");
      }
      setBlocker(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!canView) return null;

  const ready = preflight?.ready;
  const missingCount = preflight?.missing_items?.length || 0;

  return (
    <Card className="mt-4 border-slate-200 p-5" data-testid="closeout-package-card">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <div className="overline text-teal-700">Hand-over</div>
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Close-Out Package</div>
          <p className="text-xs text-slate-600">
            One ZIP with completed ITP, inspection items, survey conformance, test reports, evidence, supporting documents and audit trail.
          </p>
          {preflight?.package_identifier && (
            <p className="mt-1 text-xs text-slate-500">
              Package identifier: <span className="font-semibold text-slate-700" data-testid="closeout-pkg-identifier">{preflight.package_identifier}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {preflight && ready && (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid="closeout-status-ready">
              <CheckCircle2 className="mr-1 size-3" /> Ready
            </span>
          )}
          {preflight && !ready && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid="closeout-status-incomplete">
              <AlertTriangle className="mr-1 size-3" /> {missingCount} outstanding
            </span>
          )}
          <Button
            onClick={() => doDownload(false)}
            disabled={downloading}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="download-closeout-package-btn"
          >
            {downloading ? (
              <><Loader2 className="mr-2 size-4 animate-spin" /> Preparing…</>
            ) : (
              <><FolderArchive className="mr-2 size-4" /> Download Close-Out Package</>
            )}
          </Button>
        </div>
      </div>

      {preflight && !ready && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="closeout-preflight-warning">
          <div className="flex items-center gap-1.5 font-semibold">
            <ShieldAlert className="size-3.5" /> Outstanding close-out items
          </div>
          <ul className="mt-1 list-disc pl-5">
            {(preflight.missing_items || []).slice(0, 5).map((m, idx) => (
              <li key={idx}>
                <span className="font-semibold capitalize">{(m.type || "").replace(/_/g, " ")}:</span> {m.label}
                {m.status ? <> · <span className="italic">{m.status}</span></> : null}
              </li>
            ))}
            {missingCount > 5 && <li>+{missingCount - 5} more…</li>}
          </ul>
        </div>
      )}

      {/* Blocker Modal — surfaces when backend returns 409 */}
      <Dialog open={!!blocker} onOpenChange={(o) => !o && setBlocker(null)}>
        <DialogContent className="max-w-lg" data-testid="closeout-blocker-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="size-5 text-amber-600" />
              Close-Out Package Incomplete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">{blocker?.message}</p>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="mb-1 font-semibold">Outstanding items ({blocker?.missing_items?.length || 0}):</div>
              <ul className="list-disc pl-5" data-testid="closeout-missing-list">
                {(blocker?.missing_items || []).map((m, idx) => (
                  <li key={idx}>
                    <span className="font-semibold capitalize">{(m.type || "").replace(/_/g, " ")}:</span> {m.label}
                    {m.status ? <> · <span className="italic">{m.status}</span></> : null}
                    {m.due_date ? <> · due {m.due_date}</> : null}
                  </li>
                ))}
              </ul>
            </div>
            {blocker?.can_force ? (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
                As <b>Admin / QA Manager</b>, you can force a download for hand-over.<br/>
                A <code className="rounded bg-white px-1">Missing_Items_Register.txt</code> will be included and an audit trail entry will record the forced download.
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                Please resolve the outstanding items above. Only Admin or QA Manager can force download an incomplete package.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocker(null)} data-testid="closeout-blocker-cancel">Cancel</Button>
            {blocker?.can_force && (
              <Button
                onClick={() => doDownload(true)}
                disabled={downloading}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="closeout-blocker-force-btn"
              >
                {downloading ? <><Loader2 className="mr-2 size-4 animate-spin"/>Downloading…</> : <><FolderArchive className="mr-2 size-4"/>Force Download Anyway</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
